import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";

import apiClient from "#/redux/api/ApiClient";
import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/seme/morfologija";

export const createSeedMorphology = createAsyncThunk(
  "seedMorphology/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(baseUrl, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedMorphologyById = createAsyncThunk(
  "seedMorphology/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedMorphologyBySeedAnalysisId = createAsyncThunk(
  "seedMorphology/getBySeedAnalysisId",
  async (id, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().where("vzorecId", "EQ", id);
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        null,
        filter,
        null,
        false
      );
      if (response.data.content?.length === 0) {
        return null;
      }
      return response.data.content?.[0];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedMorphologyPage = createAsyncThunk(
  "seedMorphology/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedMorphologyPageBySeedAnalysisId = createAsyncThunk(
  "seedMorphology/getBySeedAnalysisIdList",
  async ({ seedAnalysisIdList }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().or((orGroup) => {
        seedAnalysisIdList.map((eventId) => {
          orGroup.equal("vzorecId", eventId);
        });
      });

      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        new ApiPage().page(0).size(100),
        filter,
        null,
        false
      );
      return {
        events: response.data.content ?? [],
        seedAnalysisIds: seedAnalysisIdList,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSeedMorphology = createAsyncThunk(
  "seedMorphology/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`${baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  createSeedMorphology,
  updateSeedMorphology,
  getSeedMorphologyPage,
  getSeedMorphologyById,
  getSeedMorphologyBySeedAnalysisId,
];

const seedMorphologySlice = createSlice({
  name: "seedMorphology",
  initialState: {
    details: {},
    page: {},
    bySeedBasicAnalysisId: {},
    isLoading: false,
    error: null,
    draft: null,
  },
  reducers: {
    setSeedMorphologyDraft(state, action) {
      console.log("Set seed morphology draft");
      state.draft = action.payload;
    },
    resetSeedMorphologyDraft(state, _action) {
      console.log("Reset seed morphology draft");
      state.draft = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSeedMorphology.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addCase(getSeedMorphologyPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(
        getSeedMorphologyPageBySeedAnalysisId.fulfilled,
        (state, action) => {
          const { events, seedAnalysisIds } = action.payload;
          seedAnalysisIds.forEach((seedAnalysisId) => {
            state.bySeedBasicAnalysisId[seedAnalysisId] = events.filter(
              (event) => event.vzorecId === seedAnalysisId
            );
          });
        }
      )
      .addCase(updateSeedMorphology.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addCase(getSeedMorphologyById.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addCase(getSeedMorphologyBySeedAnalysisId.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addMatcher(
        isAnyOf(...thunks.map((t) => t.rejected)),
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      )
      .addMatcher(isAnyOf(...thunks.map((t) => t.pending)), (state, _) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isAnyOf(...thunks.map((t) => t.fulfilled)), (state, _) => {
        state.isLoading = false;
      });
  },
});

export const { setSeedMorphologyDraft, resetSeedMorphologyDraft } =
  seedMorphologySlice.actions;

export default seedMorphologySlice.reducer;
